import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WebLayout from '../components/WebLayout';
import InviteFriendModal from '../components/InviteFriendModal';
import { useAuth } from '../util/auth';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import MobileLeaderboard from '../components/MobileLeaderboard';
import DriverTour from '../components/DriverTour';
import '../styles/driver-custom.css';
import { dismissWalkthrough } from '../utils/socialActions';

const Leaderboard = () => {
  const theme = useTheme();
  const auth = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Walkthrough tour state
  const [runLeaderboardTour, setRunLeaderboardTour] = useState(false);

  const leaderboardTourSteps = [
    {
      element: '.leaderboard-header-section',
      title: 'Welcome to the Leaderboard!',
      content: 'This is where you compete with your friends! See who has earned the most points and bonuses. The leaderboard shows only you and your friends - it\'s a friendly competition!',
      placement: isMobile ? 'bottom' : 'bottom',
    },
    {
      element: '.leaderboard-table-section',
      title: 'Rankings & Stats',
      content: 'Here you can see everyone\'s rank, points, bonuses claimed, and total earnings. Your row is highlighted so you can easily track your position. The more bonuses you complete, the higher you\'ll climb!',
      placement: isMobile ? 'bottom' : 'top',
    },
    {
      element: '.leaderboard-invite-button',
      title: 'Keep Growing',
      content: 'Invite more friends to make the competition even more exciting! That\'s it for the walkthrough - you\'re all set to start maximizing your bank bonuses. Happy hunting!',
      placement: isMobile ? 'bottom' : 'left',
    },
  ];

  useEffect(() => {
    const initializeUserData = async (userId, userEmail) => {
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            email: userEmail,
            points: 0,
            completedBonuses: 0,
            totalEarned: 0,
            createdAt: new Date().toISOString(),
          });
        }
        return true;
      } catch (err) {
        console.error('Error initializing user data:', err);
        return false;
      }
    };

    const fetchFriendsData = async (userId) => {
      try {
        // Get user's accepted friends from the friends collection
        const friendsRef = collection(db, 'friends');
        const friendsQuery = query(
          friendsRef,
          where('users', 'array-contains', userId),
          where('status', '==', 'accepted')
        );
        const friendsSnapshot = await getDocs(friendsQuery);
        
        // Extract all unique user IDs from the friends documents
        const friendIds = new Set();
        friendsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          data.users.forEach(uid => {
            if (uid !== userId) {
              friendIds.add(uid);
            }
          });
        });
        
        // Include current user's ID
        friendIds.add(userId);
        
        return Array.from(friendIds);
      } catch (err) {
        console.error('Error fetching friends:', err);
        return [userId]; // Return at least current user ID if friends fetch fails
      }
    };

    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Initialize current user's data if they're logged in
        if (auth.user?.uid && auth.user?.email) {
          await initializeUserData(auth.user.uid, auth.user.email);
        }

        // Get user's friends
        const friendIds = await fetchFriendsData(auth.user.uid);

        // Firestore 'in' operator only supports up to 10 items
        // If more than 10, we need to batch the queries
        let allUsers = [];

        if (friendIds.length === 0) {
          // No friends, show empty state
          setLeaderboardData([]);
          return;
        }

        // Split friendIds into chunks of 10
        const chunks = [];
        for (let i = 0; i < friendIds.length; i += 10) {
          chunks.push(friendIds.slice(i, i + 10));
        }

        // Query each chunk and combine results
        for (const chunk of chunks) {
          const usersRef = collection(db, 'users');
          const q = query(
            usersRef,
            where('__name__', 'in', chunk)
          );
          const querySnapshot = await getDocs(q);

          querySnapshot.docs.forEach(doc => {
            allUsers.push({
              id: doc.id,
              ...doc.data()
            });
          });
        }

        // Sort all users by points and totalEarned
        allUsers.sort((a, b) => {
          if (b.points !== a.points) {
            return (b.points || 0) - (a.points || 0);
          }
          return (b.totalEarned || 0) - (a.totalEarned || 0);
        });

        // Limit to top 50
        allUsers = allUsers.slice(0, 50);

        if (allUsers.length === 0) {
          // If no users found, initialize with current user
          if (auth.user?.uid) {
            setLeaderboardData([{
              id: auth.user.uid,
              rank: 1,
              name: auth.user.email?.split('@')[0] || 'Anonymous',
              points: 0,
              bonusesClaimed: 0,
              totalEarned: 0,
              isFriend: false,
            }]);
          } else {
            setLeaderboardData([]);
          }
          return;
        }

        const users = allUsers.map((user, index) => {
          const isFriend = user.id !== auth.user.uid && friendIds.includes(user.id);
          return {
            id: user.id,
            rank: index + 1,
            name: user.email?.split('@')[0] || 'Anonymous',
            points: parseInt(user.points || 0),
            bonusesClaimed: parseInt(user.completedBonuses || 0),
            totalEarned: parseInt(user.totalEarned || 0),
            isFriend,
          };
        });

        setLeaderboardData(users);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        if (err.code === 'permission-denied') {
          setError('You do not have permission to view the leaderboard');
        } else if (err.code === 'not-found') {
          setError('Leaderboard data not found');
        } else {
          setError('An error occurred while loading the leaderboard');
        }
      } finally {
        setLoading(false);
      }
    };

    const handleRefresh = () => {
      if (auth.user) {
        fetchLeaderboardData();
      }
    };

    // Listen for refresh events
    window.addEventListener('refreshLeaderboard', handleRefresh);

    // Initial fetch
    if (auth.user) {
      fetchLeaderboardData();
    } else {
      setLoading(false);
      setLeaderboardData([]);
    }

    return () => {
      window.removeEventListener('refreshLeaderboard', handleRefresh);
      setLeaderboardData([]);
      setError(null);
    };
  }, [auth.user]);

  // Check if we should continue the walkthrough tour
  useEffect(() => {
    const continueWalkthrough = sessionStorage.getItem('continueWalkthroughLeaderboard');
    console.log('Leaderboard walkthrough check:', { continueWalkthrough, loading, dataLength: leaderboardData.length });

    if (continueWalkthrough === 'true' && !loading) {
      // Delay to ensure all elements are rendered
      const timer = setTimeout(() => {
        console.log('Starting Leaderboard tour');
        setRunLeaderboardTour(true);
        // Clear the flag so tour doesn't restart on page refresh
        sessionStorage.removeItem('continueWalkthroughLeaderboard');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, leaderboardData]);

  // Handle walkthrough callback
  const handleLeaderboardTourCallback = async (data) => {
    const { status } = data;

    // When tour finishes or is skipped, dismiss walkthrough
    if (status === 'finished' || status === 'skipped') {
      setRunLeaderboardTour(false);
      sessionStorage.removeItem('continueWalkthrough');
      sessionStorage.removeItem('continueWalkthroughFriends');
      sessionStorage.removeItem('continueWalkthroughLeaderboard');

      // Mark walkthrough as complete in Firestore
      if (auth.user?.uid) {
        try {
          await dismissWalkthrough(auth.user.uid);
          await auth.refreshUserData();
        } catch (error) {
          console.error('Error dismissing walkthrough:', error);
        }
      }
    }
  };

  const content = (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.background.default,
        p: isMobile ? 0 : 4,
      }}
    >
      {/* Walkthrough Tour */}
      <DriverTour
        steps={leaderboardTourSteps}
        run={runLeaderboardTour}
        onCallback={handleLeaderboardTourCallback}
        showProgress={true}
        showButtons={['next', 'previous', 'close']}
      />

      {isMobile ? (
        <MobileLeaderboard
          leaderboardData={leaderboardData}
          loading={loading}
          error={error}
          onInviteFriend={() => setInviteModalOpen(true)}
          currentUserId={auth.user?.uid}
        />
      ) : (
        <Stack spacing={3}>
          {/* Header */}
          <Stack
            className="leaderboard-header-section"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Leaderboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Compete with friends in bank bonus hunting
              </Typography>
            </Stack>

            <Button
              className="leaderboard-invite-button"
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteModalOpen(true)}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }}
            >
              Invite Friend
            </Button>
          </Stack>

          {/* Leaderboard Table */}
          <TableContainer
            className="leaderboard-table-section"
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="10%" sx={{ fontWeight: 600 }}>Rank</TableCell>
                  <TableCell width="30%" sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell width="15%" align="right" sx={{ fontWeight: 600 }}>Points</TableCell>
                  <TableCell width="20%" align="right" sx={{ fontWeight: 600 }}>Bonuses Claimed</TableCell>
                  <TableCell width="25%" align="right" sx={{ fontWeight: 600 }}>Total Earned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'error.main' }}>
                      {error}
                    </TableCell>
                  </TableRow>
                ) : leaderboardData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      Invite friends to start competing!
                    </TableCell>
                  </TableRow>
                ) : (
                  leaderboardData.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        backgroundColor: user.id === auth?.user?.uid 
                          ? alpha(theme.palette.primary.main, 0.04)
                          : user.isFriend
                            ? alpha(theme.palette.success.main, 0.02)
                            : 'inherit',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={`#${user.rank}`}
                          size="small"
                          sx={{
                            backgroundColor: user.rank <= 3 
                              ? alpha(theme.palette.primary.main, 0.08)
                              : alpha(theme.palette.text.secondary, 0.08),
                            color: user.rank <= 3 
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: user.isFriend
                                ? alpha(theme.palette.success.main, 0.08)
                                : alpha(theme.palette.primary.main, 0.08),
                              color: user.isFriend
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                            }}
                          >
                            {user.name.charAt(0)}
                          </Avatar>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {user.name}
                            </Typography>
                            {user.isFriend && (
                              <Chip
                                label="Friend"
                                size="small"
                                sx={{
                                  height: 20,
                                  backgroundColor: alpha(theme.palette.success.main, 0.08),
                                  color: theme.palette.success.main,
                                }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                          {user.points.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={user.bonusesClaimed}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.08),
                            color: theme.palette.success.main,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          ${user.totalEarned.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {/* Invite Friend Modal */}
      <InviteFriendModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />
    </Box>
  );

  return isMobile ? content : <WebLayout>{content}</WebLayout>;
};

export default Leaderboard;